import { TagCreateInput, TagGroup } from '@vivport/backend/types';
import { useState } from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { TagFormValues, Work4MeTagForm } from './Work4MeTagForm';

interface Work4MeTagCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTagCreated: (input: TagCreateInput) => Promise<void>;
  tagGroups: TagGroup[];
}

const EMPTY_CREATE_VALUES: TagFormValues = {
  name: '',
  description: '',
  scope: 'PRIVATE',
  tagGroupId: null,
  tagOrder: undefined,
};

export function Work4MeTagCreate({ open, onOpenChange, onTagCreated, tagGroups }: Work4MeTagCreateProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSheetOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent
        className="w-screen sm:w-[540px] flex flex-col px-0 no-close-borders"
        aria-describedby={undefined}
      >
        <SheetHeader className="px-4">
          <SheetTitle>Create New Tag</SheetTitle>
          <SheetDescription>
            Add a new tag to organize your content. Tags help categorize and filter content for better organization.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 px-4 py-4 overflow-y-auto">
          <Work4MeTagForm
            initialValues={EMPTY_CREATE_VALUES}
            tagGroups={tagGroups}
            isSubmitting={isSubmitting}
            submitLabel="Create Tag"
            submittingLabel="Creating..."
            onSubmit={async (values) => {
              setIsSubmitting(true);
              try {
                await onTagCreated({
                  name: values.name,
                  description: values.description === '' ? null : values.description,
                  scope: values.scope,
                  tagGroupId: values.tagGroupId,
                  tagOrder: values.tagOrder,
                });
                onOpenChange(false);
              } finally {
                setIsSubmitting(false);
              }
            }}
          />
        </div>

        <SheetFooter className="px-4">
          <div className="w-full">
            <Separator className="mb-4" />
            <p className="text-xs text-muted-foreground">Fill all required fields to create a tag.</p>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

